import React, { useMemo } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
} from "@mui/x-data-grid";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import BlockIcon from "@mui/icons-material/Block";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import dayjs from "dayjs";

const theme = createTheme({
  components: {
    MuiDataGrid: {
      styleOverrides: {
        root: {
          "& .MuiDataGrid-row": { minHeight: "auto" },
        },
      },
      defaultProps: { density: "compact" },
    },
  },
});

function CustomToolbar() {
  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarDensitySelector />
      <GridToolbarExport />
    </GridToolbarContainer>
  );
}

const SipTraceErrorDashboard = ({ errors, onViewMessage, isDark = false }) => {
  const emptyIconColor = isDark ? "#8899aa" : "#9e9e9e";
  const errorRowBg = isDark ? "rgba(244, 67, 54, 0.12)" : "rgba(244, 67, 54, 0.04)";
  // Categorize errors
  const errorStats = useMemo(() => {
    const stats = {
      total: errors.length,
      client4xx: 0,
      server5xx: 0,
      byCode: {},
    };

    errors.forEach((err) => {
      const code = parseInt(err.status_code || 0);
      if (code >= 400 && code < 500) stats.client4xx++;
      if (code >= 500) stats.server5xx++;

      const key = `${err.status_code} ${err.status_text || ""}`.trim();
      stats.byCode[key] = (stats.byCode[key] || 0) + 1;
    });

    return stats;
  }, [errors]);

  // Error breakdown rows for the summary table
  const breakdownRows = useMemo(() => {
    return Object.entries(errorStats.byCode)
      .map(([code, count], idx) => ({
        id: idx,
        statusCode: code,
        count,
      }))
      .sort((a, b) => b.count - a.count);
  }, [errorStats]);

  const columns = [
    {
      field: "timestamp",
      headerName: "Time",
      width: 120,
      renderCell: (params) => {
        const ts = params.row.timestamp;
        return ts ? dayjs(ts).format("HH:mm:ss.SSS") : "";
      },
    },
    {
      field: "status_code",
      headerName: "Status",
      width: 100,
      renderCell: (params) => {
        const code = parseInt(params.value || 0);
        return (
          <Chip
            label={`${params.value} ${params.row.status_text || ""}`}
            size="small"
            color={code >= 500 ? "error" : "warning"}
            sx={{ fontSize: "0.7rem", height: 22 }}
          />
        );
      },
    },
    {
      field: "method",
      headerName: "Method",
      width: 100,
    },
    {
      field: "from_user",
      headerName: "From",
      width: 140,
    },
    {
      field: "to_user",
      headerName: "To",
      width: 140,
    },
    {
      field: "call_id",
      headerName: "Call-ID",
      width: 200,
      renderCell: (params) => {
        const cid = params.value || "";
        return (
          <Typography variant="body2" noWrap title={cid}>
            {cid.length > 25 ? cid.substring(0, 25) + "..." : cid}
          </Typography>
        );
      },
    },
    {
      field: "remote_ip",
      headerName: "Remote IP",
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2" noWrap>
          {params.row.remote_ip}:{params.row.remote_port}
        </Typography>
      ),
    },
    {
      field: "carrier",
      headerName: "Carrier",
      width: 120,
    },
  ];

  const rows = errors.map((err, idx) => ({
    ...err,
    id: err.id || idx,
  }));

  if (errors.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: "center" }}>
        <ErrorOutlineIcon sx={{ fontSize: 48, color: emptyIconColor, mb: 1 }} />
        <Typography color="text.secondary">
          No SIP errors captured. Errors (4xx/5xx responses) will appear here during capture.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      {/* Summary Cards */}
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap">
        <Card sx={{ minWidth: 160, flex: 1 }}>
          <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <ErrorOutlineIcon color="error" />
              <Box>
                <Typography variant="h5" fontWeight="bold">
                  {errorStats.total}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total Errors
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <Card sx={{ minWidth: 160, flex: 1 }}>
          <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <ReportProblemIcon sx={{ color: "#ff9800" }} />
              <Box>
                <Typography variant="h5" fontWeight="bold">
                  {errorStats.client4xx}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Client Errors (4xx)
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <Card sx={{ minWidth: 160, flex: 1 }}>
          <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <BlockIcon color="error" />
              <Box>
                <Typography variant="h5" fontWeight="bold">
                  {errorStats.server5xx}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Server Errors (5xx)
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Error breakdown mini-table */}
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary" fontWeight="bold">
              Error Breakdown
            </Typography>
            {breakdownRows.slice(0, 5).map((row) => (
              <Stack
                key={row.statusCode}
                direction="row"
                justifyContent="space-between"
                sx={{ mt: 0.5 }}
              >
                <Typography variant="body2">{row.statusCode}</Typography>
                <Chip label={row.count} size="small" sx={{ height: 18, fontSize: "0.7rem" }} />
              </Stack>
            ))}
          </CardContent>
        </Card>
      </Stack>

      {/* Error Messages Grid */}
      <ThemeProvider theme={theme}>
        <DataGrid
          rows={rows}
          columns={columns}
          autoHeight
          pageSizeOptions={[25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
            sorting: { sortModel: [{ field: "timestamp", sort: "asc" }] },
          }}
          slots={{ toolbar: CustomToolbar }}
          disableRowSelectionOnClick
          onRowClick={(params) => onViewMessage(params.row.id)}
          sx={{
            "& .MuiDataGrid-row": {
              bgcolor: errorRowBg,
            },
            "& .MuiDataGrid-row:hover": {
              cursor: "pointer",
            },
          }}
        />
      </ThemeProvider>
    </Box>
  );
};

export default SipTraceErrorDashboard;
